import { useEffect, useRef } from "react";
import * as THREE from "three";

const EARTH_TEXTURE_URL = "/textures/earth-atmos-2048.jpg";
const EARTH_SPECULAR_URL = "/textures/earth-specular-2048.jpg";
const EARTH_NORMAL_URL = "/textures/earth-normal-2048.jpg";
const EARTH_CLOUDS_URL = "/textures/earth-clouds-1024.png";
const RADIUS = 2.18;
const DEFAULT_ROTATION = { x: 0.56, y: -Math.PI / 2 };

const DESTINATIONS = [
  { label: "London", lat: 51.5074, lon: -0.1278, home: true, labelShift: { east: -0.11, north: 0.12 } },
  { label: "Amsterdam", lat: 52.3676, lon: 4.9041, labelShift: { east: 0.25, north: 0.16 } },
  { label: "Marbella", lat: 36.5101, lon: -4.8824, labelShift: { east: -0.16, north: -0.06 } },
  { label: "Ibiza", lat: 38.9067, lon: 1.4206, labelShift: { east: 0.25, north: -0.1 } },
  { label: "Malta", lat: 35.9375, lon: 14.3754, labelShift: { east: 0.3, north: -0.1 } },
];

function latLonToPoint(lat, lon, radius = RADIUS) {
  const latitude = THREE.MathUtils.degToRad(lat);
  const longitude = THREE.MathUtils.degToRad(lon);
  const cosLatitude = Math.cos(latitude);

  return new THREE.Vector3(
    radius * cosLatitude * Math.cos(longitude),
    radius * Math.sin(latitude),
    -radius * cosLatitude * Math.sin(longitude)
  );
}

function labelOffsetVector(destination) {
  const latitude = THREE.MathUtils.degToRad(destination.lat);
  const longitude = THREE.MathUtils.degToRad(destination.lon);
  const east = new THREE.Vector3(-Math.sin(longitude), 0, -Math.cos(longitude)).normalize();
  const north = new THREE.Vector3(
    -Math.sin(latitude) * Math.cos(longitude),
    Math.cos(latitude),
    Math.sin(latitude) * Math.sin(longitude)
  ).normalize();

  return east
    .multiplyScalar(destination.labelShift.east)
    .add(north.multiplyScalar(destination.labelShift.north));
}

function makeArc(start, end, radius = RADIUS, lift = 0.14) {
  const startVector = latLonToPoint(start.lat, start.lon, radius + 0.035);
  const endVector = latLonToPoint(end.lat, end.lon, radius + 0.035);
  const points = [];

  for (let index = 0; index <= 72; index += 1) {
    const t = index / 72;
    const point = startVector.clone().lerp(endVector, t).normalize();
    point.multiplyScalar(radius + 0.035 + Math.sin(Math.PI * t) * lift);
    points.push(point);
  }

  return new THREE.BufferGeometry().setFromPoints(points);
}

function createLabelTexture(label) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const context = canvas.getContext("2d");

  context.font = "700 42px Montserrat, Arial, sans-serif";
  context.textBaseline = "middle";
  context.shadowColor = "rgba(0,0,0,0.95)";
  context.shadowBlur = 16;
  context.shadowOffsetY = 5;
  context.fillStyle = "#f5d866";
  context.fillText(label.toUpperCase(), 18, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}

export default function EventsEuropeGlobe() {
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const shell = shellRef.current;
    const canvas = canvasRef.current;
    if (!shell || !canvas) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(31, 1, 0.1, 100);
    camera.position.set(0, 0, 6.3);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas,
      powerPreference: "high-performance",
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.16;

    const gold = new THREE.Color("#d4af37");
    const stage = new THREE.Group();
    const globe = new THREE.Group();
    globe.rotation.set(DEFAULT_ROTATION.x, DEFAULT_ROTATION.y, 0);
    stage.add(globe);
    scene.add(stage);

    const textureLoader = new THREE.TextureLoader();
    const globeTexture = textureLoader.load(EARTH_TEXTURE_URL);
    const specularTexture = textureLoader.load(EARTH_SPECULAR_URL);
    const normalTexture = textureLoader.load(EARTH_NORMAL_URL);
    const cloudsTexture = textureLoader.load(EARTH_CLOUDS_URL);
    const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
    [globeTexture, specularTexture, normalTexture, cloudsTexture].forEach((texture) => {
      texture.anisotropy = maxAnisotropy;
    });
    globeTexture.colorSpace = THREE.SRGBColorSpace;
    cloudsTexture.colorSpace = THREE.SRGBColorSpace;

    const earth = new THREE.Mesh(
      new THREE.SphereGeometry(RADIUS, 128, 128),
      new THREE.MeshPhongMaterial({
        map: globeTexture,
        bumpMap: normalTexture,
        bumpScale: 0.048,
        specularMap: specularTexture,
        specular: new THREE.Color("#2f6086"),
        shininess: 20,
      })
    );
    globe.add(earth);

    const clouds = new THREE.Mesh(
      new THREE.SphereGeometry(RADIUS * 1.012, 96, 96),
      new THREE.MeshBasicMaterial({
        map: cloudsTexture,
        opacity: 0.22,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    globe.add(clouds);

    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(RADIUS * 1.06, 96, 96),
      new THREE.ShaderMaterial({
        side: THREE.BackSide,
        transparent: true,
        blending: THREE.AdditiveBlending,
        uniforms: { glowColor: { value: gold } },
        vertexShader: `
          varying vec3 vNormal;
          void main() {
            vNormal = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 glowColor;
          varying vec3 vNormal;
          void main() {
            float intensity = pow(0.72 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
            gl_FragColor = vec4(glowColor, intensity * 0.48);
          }
        `,
      })
    );
    globe.add(atmosphere);

    scene.add(new THREE.AmbientLight(0x8a8f99, 0.58));
    const sun = new THREE.DirectionalLight(0xfff0c2, 2.7);
    sun.position.set(-2.8, 2.4, 4.4);
    scene.add(sun);
    const rim = new THREE.DirectionalLight(0xd4af37, 1.65);
    rim.position.set(3.2, 0.8, 1.8);
    scene.add(rim);

    const home = DESTINATIONS.find((destination) => destination.home);
    const routeMaterial = new THREE.LineBasicMaterial({
      color: gold,
      opacity: 0.62,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    DESTINATIONS.filter((destination) => !destination.home).forEach((destination) => {
      globe.add(new THREE.Line(makeArc(home, destination), routeMaterial));
    });

    const markerGeometry = new THREE.SphereGeometry(0.052, 28, 28);
    const markerMaterial = new THREE.MeshBasicMaterial({ color: "#ff2e22" });
    const markers = DESTINATIONS.map((destination, index) => {
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.position.copy(latLonToPoint(destination.lat, destination.lon, RADIUS + 0.065));
      marker.userData = { index };
      globe.add(marker);
      return marker;
    });

    const labelTextures = [];
    DESTINATIONS.forEach((destination) => {
      const labelTexture = createLabelTexture(destination.label);
      labelTextures.push(labelTexture);
      const label = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: labelTexture,
          opacity: 0.9,
          transparent: true,
          depthTest: true,
          depthWrite: false,
        })
      );
      const surfacePoint = latLonToPoint(destination.lat, destination.lon, RADIUS + 0.17);
      label.position.copy(surfacePoint.add(labelOffsetVector(destination)));
      label.scale.set(0.52, 0.13, 1);
      globe.add(label);
    });

    let frame = 0;
    const targetRotation = { x: DEFAULT_ROTATION.x, y: DEFAULT_ROTATION.y };

    const resize = () => {
      const rect = shell.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.position.z = width < 420 ? 6.45 : 5.72;
      camera.updateProjectionMatrix();
      stage.position.set(0.03, -0.2, -0.08);
      stage.scale.setScalar(width < 420 ? 1.06 : 1.2);
    };

    const onPointerMove = (event) => {
      const rect = shell.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
      pointerRef.current.x = Math.max(-1, Math.min(1, x));
      pointerRef.current.y = Math.max(-1, Math.min(1, y));
      targetRotation.y = DEFAULT_ROTATION.y + pointerRef.current.x * 0.48;
      targetRotation.x = DEFAULT_ROTATION.x + pointerRef.current.y * 0.18;
    };

    const onPointerLeave = () => {
      pointerRef.current.x = 0;
      pointerRef.current.y = 0;
      targetRotation.x = DEFAULT_ROTATION.x;
      targetRotation.y = DEFAULT_ROTATION.y;
    };

    const animate = () => {
      frame = window.requestAnimationFrame(animate);
      const time = performance.now() * 0.001;
      const { x, y } = pointerRef.current;

      camera.position.x += (x * 0.14 - camera.position.x) * 0.05;
      camera.position.y += (-y * 0.08 - camera.position.y) * 0.05;
      camera.lookAt(0, 0, 0);

      globe.rotation.y += (targetRotation.y + Math.sin(time * 0.24) * 0.01 - globe.rotation.y) * 0.08;
      globe.rotation.x += (targetRotation.x - globe.rotation.x) * 0.08;
      globe.rotation.z = x * 0.01;
      clouds.rotation.y = time * 0.015;

      markers.forEach((marker) => {
        const scale = 1 + Math.sin(time * 2.5 + marker.userData.index * 0.68) * 0.12;
        marker.scale.setScalar(scale);
      });

      renderer.render(scene, camera);
    };

    resize();
    animate();
    shell.addEventListener("pointermove", onPointerMove);
    shell.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", resize);

    return () => {
      window.cancelAnimationFrame(frame);
      shell.removeEventListener("pointermove", onPointerMove);
      shell.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", resize);
      globeTexture.dispose();
      specularTexture.dispose();
      normalTexture.dispose();
      cloudsTexture.dispose();
      labelTextures.forEach((texture) => texture.dispose());
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) object.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={shellRef}
      className="relative h-[18rem] min-h-[18rem] overflow-hidden border border-white/10 bg-black md:h-[20rem]"
    >
      <canvas
        ref={canvasRef}
        data-events-europe-globe
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_46%,rgba(212,175,55,0.13),transparent_42%),linear-gradient(180deg,rgba(0,0,0,0.08),rgba(0,0,0,0.64))]" />
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-300">
        <span>Europe circuit</span>
        <span className="text-brand-500">5 locations</span>
      </div>
    </div>
  );
}
